import { useState } from 'react';
import { LuPlus } from 'react-icons/lu';

import { Account, Conviction, ConvictionType } from '@ndvr/portfolio-api';

import { AssetClass, AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { TickerSelectorInput } from '@/components/TickerSelectorInput';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { isExcludedAsset, isExcludedConviction } from './convictionUtils';
import { ExtendedConviction } from './types';

interface AddConvictionDialogProps {
    accounts: Account[];
    existingConvictions: Record<string, ExtendedConviction[]>;
    defaultAccountGUID?: string;
    onAdd: (conviction: Conviction, accountGUID: string, asset: AssetInfo) => void;
}

const convictionTypeLabels: Partial<Record<ConvictionType, string>> = {
    [ConvictionType.EXPOSURE]: 'Exposure',
    [ConvictionType.QUANTITY]: 'Quantity',
    [ConvictionType.EXCLUDED]: 'Excluded Position',
    // Note: WEIGHT is intentionally excluded from the dropdown
};

const getValueLabel = (convictionType: ConvictionType): string => {
    switch (convictionType) {
        case ConvictionType.WEIGHT:
            return 'Weight (%)';
        case ConvictionType.EXPOSURE:
            return 'Exposure ($)';
        case ConvictionType.QUANTITY:
            return 'Quantity (shares)';
        default:
            return 'Value';
    }
};

type ConvictionFormData = {
    conviction: Partial<Conviction>;
    asset: AssetInfo | undefined;
    accountGUID: string;
};

const makeInitialFormData = (accountGUID = ''): ConvictionFormData => ({
    conviction: { convictionType: ConvictionType.EXPOSURE },
    asset: undefined,
    accountGUID,
});

export const AddConvictionDialog = ({
    accounts,
    existingConvictions,
    defaultAccountGUID,
    onAdd,
}: AddConvictionDialogProps) => {
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState<ConvictionFormData>(() => makeInitialFormData(defaultAccountGUID));

    // Exclude only securities already added to the selected account, so the
    // same ticker can be added to different accounts.
    const excludedItems = (existingConvictions[formData.accountGUID] ?? [])
        .map((conviction) => ({
            sId: conviction.securityId,
            scId: conviction.shareClassId,
            displayText: 'Already added',
        }))
        .filter((item) => item.sId);

    // Excluded positions carry no target value; the other types need a positive one
    const hasValidValue = (conviction: Partial<Conviction>): boolean => {
        switch (conviction.convictionType) {
            case ConvictionType.EXCLUDED:
                return true;
            case ConvictionType.EXPOSURE:
                return conviction.exposure !== undefined && parseFloat(conviction.exposure) > 0;
            default:
                return conviction.quantity !== undefined && conviction.quantity > 0;
        }
    };

    const handleSubmit = () => {
        if (
            formData.asset &&
            formData.conviction.convictionType &&
            hasValidValue(formData.conviction) &&
            formData.accountGUID
        ) {
            onAdd(formData.conviction as Conviction, formData.accountGUID, formData.asset);
            // Reset form and close dialog
            setFormData(makeInitialFormData());
            setOpen(false);
        }
    };

    const canSubmit =
        formData.asset &&
        formData.conviction.convictionType &&
        hasValidValue(formData.conviction) &&
        formData.accountGUID;

    const handleOpenChange = (nextOpen: boolean) => {
        // Default the account to whichever one is expanded when the dialog opens
        if (nextOpen) {
            setFormData(makeInitialFormData(defaultAccountGUID));
        }
        setOpen(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button variant="outline">
                    <LuPlus className="mr-2 h-4 w-4" />
                    Add Conviction
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Add Conviction</DialogTitle>
                    <DialogDescription>Add a new conviction to the portfolio.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="account">Account</Label>
                        <Select
                            value={formData.accountGUID}
                            onValueChange={(value) =>
                                setFormData({ ...formData, asset: undefined, accountGUID: value })
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                                {accounts?.map((account) => (
                                    <SelectItem key={account.accountGUID} value={account.accountGUID}>
                                        {account.accountName.plainText}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="ticker">Security</Label>
                        <TickerSelectorInput
                            value={formData.asset ? `${formData.asset.symbol} - ${formData.asset.description}` : ''}
                            onSelectTicker={(asset) => setFormData({ ...formData, asset })}
                            placeholder="Select ticker"
                            assetClass={AssetClass.EQUITY}
                            excludeItems={excludedItems}
                            excludeAsset={isExcludedAsset}
                            excludeAssetDisplayText="Not investable"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="convictionType">Conviction Type</Label>
                        <Select
                            value={formData.conviction.convictionType}
                            onValueChange={(value) =>
                                setFormData({
                                    ...formData,
                                    conviction: {
                                        ...formData.conviction,
                                        convictionType: value as ConvictionType,
                                        exposure: undefined, // Reset values when type changes
                                        quantity: undefined,
                                    },
                                })
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select conviction type" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(convictionTypeLabels).map(([type, label]) =>
                                    label ? (
                                        <SelectItem key={type} value={type}>
                                            {label}
                                        </SelectItem>
                                    ) : null,
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    {formData.conviction.convictionType && isExcludedConviction(formData.conviction.convictionType) && (
                        <p className="text-muted-foreground text-sm">
                            The held position is excluded from trading: it is neither sold nor targeted by any strategy
                            and its value is left out of the growth sleeve.
                        </p>
                    )}
                    {formData.conviction.convictionType &&
                        !isExcludedConviction(formData.conviction.convictionType) && (
                            <div className="space-y-2">
                                <Label htmlFor="value">{getValueLabel(formData.conviction.convictionType)}</Label>
                                <Input
                                    id="value"
                                    type="number"
                                    value={
                                        formData.conviction.convictionType === ConvictionType.EXPOSURE
                                            ? formData.conviction.exposure || ''
                                            : formData.conviction.quantity || ''
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                                        setFormData({
                                            ...formData,
                                            conviction: {
                                                ...formData.conviction,
                                                ...(formData.conviction.convictionType === ConvictionType.EXPOSURE
                                                    ? { exposure: e.target.value }
                                                    : { quantity: value }),
                                            },
                                        });
                                    }}
                                    placeholder="Enter value"
                                />
                            </div>
                        )}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                        Add Conviction
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
